'use client'

// React Imports
import { useState, useEffect, useMemo } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import TablePagination from '@mui/material/TablePagination'
import Box from '@mui/material/Box'

// Third-party Imports
import classnames from 'classnames'
import { Icon } from '@iconify/react'
import { rankItem } from '@tanstack/match-sorter-utils'
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getFilteredRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    getPaginationRowModel,
    getSortedRowModel
} from '@tanstack/react-table'

// Style Imports
import tableStyles from '../../../../components/common/Template_Layouts/@core/styles/table.module.css'

const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value)
    addMeta({ itemRank })
    return itemRank.passed
}

const PremiumModuleTable = (props) => {
    const {
        data: tableData,
        columns,
        totalRecords,
        currentPage,
        itemsPerPage,
        onPageChange,
        onRowSelectionChange,
        rowSelection: externalRowSelection,
        onManualRowSelectionChange,
        globalFilter: externalGlobalFilter,
        onGlobalFilterChange,
        loading = false,
        isLoading = false,
        hasFetched = true,
        isInitialLoad = false,
        onItemsPerPageChange,
        onAddClick
    } = props;

    const isDataLoading = loading || isLoading;

    const onRowClick = props.onRowClick;

    const [internalRowSelection, setInternalRowSelection] = useState({})
    const rowSelection = externalRowSelection || internalRowSelection
    const setRowSelection = onManualRowSelectionChange || setInternalRowSelection

    const [globalFilter, setGlobalFilter] = useState(externalGlobalFilter || '')
    const [jumpPage, setJumpPage] = useState(currentPage || 1)

    useEffect(() => {
        setJumpPage(currentPage || 1)
    }, [currentPage])

    useEffect(() => {
        if (onRowSelectionChange) {
            onRowSelectionChange(rowSelection)
        }
    }, [rowSelection, onRowSelectionChange])

    const table = useReactTable({
        data: tableData || [],
        columns,
        filterFns: { fuzzy: fuzzyFilter },
        state: {
            rowSelection,
            globalFilter,
            pagination: {
                pageIndex: (currentPage || 1) - 1,
                pageSize: itemsPerPage || 10
            }
        },
        manualPagination: true,
        getRowId: row => row.id || row.Id || row.idNum || String(row.index || ''), 
        pageCount: Math.ceil((totalRecords || 0) / (itemsPerPage || 10)),
        enableSorting: true,
        enableRowSelection: true,
        globalFilterFn: fuzzyFilter,
        onRowSelectionChange: setRowSelection,
        getCoreRowModel: getCoreRowModel(),
        onGlobalFilterChange: (val) => {
            setGlobalFilter(val)
            if (onGlobalFilterChange) onGlobalFilterChange(val)
        },
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues()
    })

    const startIndex = ((currentPage || 1) - 1) * (itemsPerPage || 10);
    const slicedRows = table.getRowModel().rows;

    return (
        <>
            <style>{`
            .premium-table-scroll-container::-webkit-scrollbar { width: 10px; height: 10px; }
            .premium-table-scroll-container::-webkit-scrollbar-track { background: #f8fafc; }
            .premium-table-scroll-container::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 5px; border: 2px solid #f8fafc; }
            .premium-table-scroll-container::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            .premium-table-row:hover { background-color: #f8fafc; }
        `}</style>
            <Card sx={{
                boxShadow: 'none', border: 'none', borderBottom: '1px solid #ebedf2',
                borderRadius: 0, width: '100%', margin: 0,
                display: 'flex', flexDirection: 'column',
                height: '100%',          
                flex: 1,                 
                minHeight: 0,            
                overflow: 'hidden'       
            }}>
                <div className="premium-table-scroll-container" style={{
                    flex: 1,
                    overflowY: 'scroll', 
                    overflowX: 'auto',
                    minHeight: 0,
                    backgroundColor: '#fff'
                }}>
                    <table className={tableStyles.table} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#ffffff' }}>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <th key={header.id} style={{
                                            padding: '12px 16px',
                                            paddingLeft: header.id === 'select' ? '16px' : '16px',
                                            width: header.id === 'select' ? '52px' : 'auto',
                                            textAlign: 'left',
                                            backgroundColor: '#ffffff',
                                            borderBottom: '1px solid #e2e8f0'
                                        }}>
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={classnames('flex items-center select-none w-full gap-2', {
                                                        'cursor-pointer select-none': header.column.getCanSort(),
                                                        'justify-center': header.id === 'action',
                                                        'justify-between': header.id !== 'action'
                                                    })}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    <div className='text-[13px] font-bold uppercase tracking-wider text-[#666666]'>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </div>
                                                    {header.column.getCanSort() && (
                                                        <div
                                                            title={
                                                                header.column.getIsSorted() === 'asc'
                                                                    ? 'Sort descending'
                                                                    : header.column.getIsSorted() === 'desc'
                                                                        ? 'Sort ascending'
                                                                        : 'Sort by ascending/descending'
                                                            }
                                                            className={classnames('flex items-center transition-all duration-200', {
                                                                'text-[#3b82f6] opacity-100 scale-110 font-bold': header.column.getIsSorted(),
                                                                'text-[#64748b] opacity-100 hover:opacity-100 hover:text-[#3b82f6]': !header.column.getIsSorted()
                                                            })}
                                                        >
                                                            {{
                                                                asc: <Icon icon='ri:arrow-up-s-fill' fontSize={22} />,
                                                                desc: <Icon icon='ri:arrow-down-s-fill' fontSize={22} />
                                                            }[header.column.getIsSorted()] ?? <Icon icon='ri:arrow-up-down-fill' fontSize={22} />}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        {isDataLoading ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length + 1} className='text-center py-10'>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                            <div style={{
                                                border: '3px solid #e2e8f0',
                                                borderTopColor: '#2563eb',
                                                borderRadius: '50%',
                                                width: '40px',
                                                height: '40px',
                                                animation: 'premium-table-spin 1s linear infinite',
                                                marginBottom: '16px'
                                            }} />
                                            <style>{`
                                                @keyframes premium-table-spin {
                                                    0% { transform: rotate(0deg); }
                                                    100% { transform: rotate(360deg); }
                                                }
                                            `}</style>
                                            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#444' }}>
                                                Loading...
                                            </Typography>
                                        </Box>
                                    </td>
                                </tr>
                            </tbody>
                        ) : tableData.length === 0 && hasFetched ? (
                            <tbody>
                                <tr>
                                    <td colSpan={columns.length + 1} className='text-center py-10'>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                                            <img
                                                src="/nodata.gif"
                                                alt="No Record Found"
                                                style={{ width: '350px', height: 'auto', marginBottom: '16px' }}
                                            />
                                            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#444', mb: 1 }}>
                                                No record Found.
                                            </Typography>
                                            {onAddClick && (
                                                <Typography
                                                    onClick={onAddClick}
                                                    sx={{
                                                        fontSize: '14px',
                                                        color: '#2563eb',
                                                        fontWeight: 500,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                            color: '#1d4ed8'
                                                        }
                                                    }}
                                                >
                                                    Click here to add your first credit note now <Icon icon="tabler:square-plus" fontSize={18} style={{ verticalAlign: 'middle' }} />
                                                </Typography>
                                            )}
                                        </Box>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                {slicedRows.map(row => (
                                    <tr
                                        key={row.id}
                                        className={classnames('premium-table-row', { selected: row.getIsSelected() })}
                                        onClick={() => props.onRowClick && props.onRowClick(row)}
                                        style={{ cursor: props.onRowClick ? 'pointer' : 'default' }}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td
                                                key={cell.id}
                                                style={{
                                                    padding: '8px 16px',
                                                    paddingLeft: cell.column.id === 'select' ? '16px' : '16px',
                                                    width: cell.column.id === 'select' ? '52px' : 'auto',
                                                    borderBottom: '1px solid #ebedf2'
                                                }}
                                                className={classnames({ 'text-center': cell.column.id === 'action' })}
                                            >
                                                <Box
                                                    onClick={cell.column.id === 'select' || cell.column.id === 'action' ? (e) => e.stopPropagation() : undefined}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </Box>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        )}
                    </table>
                </div>
                <div className='flex items-center justify-end px-4 gap-4 border-t py-2' style={{ backgroundColor: '#fcfcfc' }}>
                    <div className='flex items-center gap-2'>
                        <Typography variant='body2' className='text-xs opacity-70'>
                            Go to page
                        </Typography>
                        <input
                            type='number'
                            value={jumpPage}
                            min={1}
                            max={Math.ceil((totalRecords || 0) / (itemsPerPage || 10))}
                            onChange={e => setJumpPage(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const maxPage = Math.ceil((totalRecords || 0) / (itemsPerPage || 10));
                                    const page = Math.max(1, Math.min(Number(jumpPage), maxPage));
                                    onPageChange && onPageChange(page);
                                    setJumpPage(page);
                                    e.target.blur();
                                }
                            }}
                            onBlur={e => {
                                const pageCount = Math.ceil((totalRecords || 0) / (itemsPerPage || 10));
                                const page = Math.max(1, Math.min(Number(e.target.value), pageCount > 0 ? pageCount : 1))
                                if (!isNaN(page)) {
                                    onPageChange && onPageChange(page)
                                    setJumpPage(page)
                                }
                            }}
                            style={{
                                width: '56px',
                                height: '28px',
                                textAlign: 'center',
                                fontSize: '13px',
                                border: '1px solid #dcdfe6',
                                borderRadius: '4px',
                                outline: 'none',
                                padding: '0 4px',
                                MozAppearance: 'textfield',
                            }}
                            className='jump-page-input'
                        />
                    </div>
                    <TablePagination
                        rowsPerPageOptions={[]}
                        component='div'
                        count={totalRecords || 0}
                        rowsPerPage={itemsPerPage || 10}
                        page={(currentPage || 1) - 1}
                        onPageChange={(_, page) => onPageChange && onPageChange(page + 1)}
                        onRowsPerPageChange={e => {
                            const val = Number(e.target.value);
                            if (onItemsPerPageChange) onItemsPerPageChange(val);
                        }}
                        nextIconButtonProps={(globalFilter || externalGlobalFilter) ? { disabled: true } : undefined}
                        sx={{
                            border: 'none',
                            '& .MuiTablePagination-toolbar': { minHeight: '40px' },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-select, & .MuiTablePagination-selectIcon, & .MuiInputBase-root': { display: 'none !important' },
                            '& .MuiTablePagination-displayedRows': { fontSize: '12px', opacity: 0.7, margin: 0 }
                        }}
                    />
                </div>
            </Card>
        </>
    )
}

export default PremiumModuleTable
